/**
 * AI Mastery Belt System — Duolingo-grade progression psychology.
 *
 * Belts are derived purely from XP (no backend changes). Each belt
 * unlocks visual identity, badges, and aspirational copy.
 */
import React from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";

export const BELTS = [
  { id: "white",  name: "White",  rank: 1, title: "Beginner",      xp: 0,    color: "#F1F5F9", stripe: "#0F1419", textOn: "#0F1419", desc: "AI fundamentals. Learn how prompts work and why specificity wins." },
  { id: "yellow", name: "Yellow", rank: 2, title: "Operator",      xp: 50,   color: "#FACC15", stripe: "#0F1419", textOn: "#0F1419", desc: "Role prompting and structured outputs. Get reliable results, every time." },
  { id: "orange", name: "Orange", rank: 3, title: "Builder",       xp: 150,  color: "#F97316", stripe: "#0F1419", textOn: "#FFFFFF", desc: "Chains and workflows. Automate repetitive tasks at work and home." },
  { id: "green",  name: "Green",  rank: 4, title: "Strategist",    xp: 300,  color: "#16A34A", stripe: "#0F1419", textOn: "#FFFFFF", desc: "Systems and logic. Use AI for productivity and business leverage." },
  { id: "blue",   name: "Blue",   rank: 5, title: "Creator",       xp: 500,  color: "#2563EB", stripe: "#0F1419", textOn: "#FFFFFF", desc: "Content systems, tools, and AI-assisted projects shipped to the world." },
  { id: "purple", name: "Purple", rank: 6, title: "Architect",     xp: 800,  color: "#7C3AED", stripe: "#0F1419", textOn: "#FFFFFF", desc: "Apps, automations, and full AI workflows. You build what others ask for." },
  { id: "brown",  name: "Brown",  rank: 7, title: "Master Operator", xp: 1200, color: "#92400E", stripe: "#FACC15", textOn: "#FFFFFF", desc: "High-level optimization. Multi-step systems with measurable ROI." },
  { id: "black",  name: "Black",  rank: 8, title: "AI Master",     xp: 1800, color: "#0F1419", stripe: "#FACC15", textOn: "#FFFFFF", desc: "Top 1%. Real-world implementation. The benchmark others measure against." },
];

export function beltForXP(xp = 0) {
  let current = BELTS[0];
  for (const b of BELTS) {
    if (xp >= b.xp) current = b;
  }
  const next = BELTS.find((b) => b.rank === current.rank + 1) || null;
  const progress = next
    ? Math.max(0, Math.min(1, (xp - current.xp) / (next.xp - current.xp)))
    : 1;
  return { current, next, progress, xp };
}

/* ───────────── BeltStripe — the visual mark of a belt ─────────────
   A horizontal belt with a contrasting accent stripe, like a real gi belt. */
export function BeltStripe({ belt, size = "md", className = "", title }) {
  const dims = {
    sm: { w: 38, h: 10, br: 3 },
    md: { w: 58, h: 14, br: 4 },
    lg: { w: 96, h: 22, br: 5 },
    xl: { w: 140, h: 32, br: 6 },
  }[size];
  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{
        width: dims.w,
        height: dims.h,
        background: belt.color,
        borderRadius: dims.br,
        boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.18), 0 1px 1px rgba(0,0,0,0.06)",
      }}
      aria-label={title || `${belt.name} belt — ${belt.title}`}
      title={title || `${belt.name} belt — ${belt.title}`}
    >
      {/* stripe band near the right end */}
      <div
        style={{
          position: "absolute",
          right: dims.w * 0.18,
          top: 1,
          bottom: 1,
          width: Math.max(2, dims.h * 0.22),
          background: belt.stripe,
          borderRadius: 1,
          opacity: belt.id === "white" ? 0.85 : 0.92,
        }}
      />
    </div>
  );
}

/* ───────────── BeltBadge — chip with belt + title text ───────────── */
export function BeltBadge({ belt, size = "md", testid }) {
  const sizing = {
    sm: { padX: "0.5rem", padY: "0.3rem", text: "0.72rem" },
    md: { padX: "0.65rem", padY: "0.35rem", text: "0.78rem" },
    lg: { padX: "0.85rem", padY: "0.45rem", text: "0.88rem" },
  }[size];
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-white"
      style={{ padding: `${sizing.padY} ${sizing.padX}` }}
      data-testid={testid}
    >
      <BeltStripe belt={belt} size={size === "lg" ? "md" : "sm"} />
      <span className="font-semibold text-[var(--ink)]" style={{ fontSize: sizing.text }}>
        {belt.name} Belt
      </span>
      <span className="text-[var(--ink-3)]" style={{ fontSize: sizing.text }}>
        · {belt.title}
      </span>
    </div>
  );
}

/* ───────────── BeltProgress — current belt with progress to next ───────────── */
export function BeltProgress({ xp = 0, compact = false }) {
  const { current, next, progress } = beltForXP(xp);
  const pct = Math.round(progress * 100);
  return (
    <div
      className={`rounded-2xl border border-[var(--line)] bg-white ${compact ? "p-4" : "p-5 md:p-6"}`}
      data-testid="belt-progress-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BeltStripe belt={current} size={compact ? "md" : "lg"} />
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.15em] font-semibold text-[var(--ink-3)]">
              Current rank
            </div>
            <div className="font-display text-lg md:text-xl leading-tight">
              {current.name} Belt · <span className="text-[var(--ink-2)] font-medium">{current.title}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[0.65rem] uppercase tracking-[0.15em] font-semibold text-[var(--ink-3)]">Rank</div>
          <div className="font-display text-2xl tabular-nums">{current.rank} / 8</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-[var(--ink-2)]">
            {next ? `Progress to ${next.name} Belt` : "Top rank reached"}
          </span>
          <span className="font-semibold text-[var(--ink)] tabular-nums">{pct}%</span>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-[var(--bg-2)] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="h-full rounded-full"
            style={{
              background: next
                ? `linear-gradient(90deg, ${current.color}, ${next.color})`
                : current.color,
            }}
          />
        </div>
        {next && (
          <div className="text-xs text-[var(--ink-3)] mt-2">
            Next up: <span className="font-medium text-[var(--ink-2)]">{next.name} Belt · {next.title}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────── BeltLadder — full progression rail ───────────── */
export function BeltLadder({ currentXP = 0, compact = false }) {
  const { current } = beltForXP(currentXP);
  return (
    <div
      className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-4"
      data-testid="belt-ladder"
    >
      {BELTS.map((b) => {
        const unlocked = currentXP >= b.xp;
        const active = b.id === current.id;
        return (
          <motion.div
            key={b.id}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: b.rank * 0.06, duration: 0.45 }}
            className={`rounded-xl ${active ? "ring-2 ring-[var(--primary)]" : "border border-[var(--line)]"} bg-white p-3 ${compact ? "" : "md:p-4"} relative`}
            data-testid={`belt-card-${b.id}`}
          >
            {!unlocked && (
              <div className="absolute top-2 right-2 text-[var(--ink-4)]">
                <Lock size={11} />
              </div>
            )}
            <BeltStripe belt={b} size="md" className={unlocked ? "" : "opacity-50"} />
            <div className={`mt-2.5 font-display text-sm md:text-base leading-tight ${unlocked ? "text-[var(--ink)]" : "text-[var(--ink-3)]"}`}>
              {b.name}
            </div>
            <div className={`text-[0.65rem] md:text-xs ${unlocked ? "text-[var(--ink-2)]" : "text-[var(--ink-4)]"}`}>
              {b.title}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
